import type { IdGenerator, SkinPresetId, UserId } from "./ids.js";

export const skinColorFields = [
  "MaleDisplayColor", "MarkingsColor", "BodyColor", "FlankColor", "UnderbellyColor",
  "TeethColor", "MouthColor", "ClawsColor", "Detail1Color", "EyesColor"
] as const;

export type SkinColorField = typeof skinColorFields[number];
export type SkinPresetStatus = "draft" | "pending_review" | "approved" | "rejected" | "disabled";
export const skinEffectModes = [
  "none", "rainbow", "bioluminescent", "lava", "toxic", "frost", "blood_moon", "aurora", "glitch",
  "ember", "deep_ocean", "phantom", "solar_flare", "verdant", "hdr_glitch", "custom", "environment"
] as const;
export const skinEffectPatterns = ["cycle", "pulse"] as const;
export const skinAnimationPatterns = ["cycle", "pulse", "cascade"] as const;
export const skinEnvironmentModes = ["day_night", "weather", "biome", "adaptive"] as const;
export type SkinEffectMode = typeof skinEffectModes[number];
export type SkinEffectPattern = typeof skinEffectPatterns[number];
export type SkinAnimationPattern = typeof skinAnimationPatterns[number];
export type SkinEnvironmentMode = typeof skinEnvironmentModes[number];
export interface SkinEffectSettings {
  effectMode: SkinEffectMode;
  effectColor1: string;
  effectColor2: string;
  effectColor3: string;
  effectSpeedSeconds: number;
  effectPattern: SkinEffectPattern;
  environmentMode: SkinEnvironmentMode;
}
export interface SkinColor { r: number; g: number; b: number; a: number }
export interface SkinAnimationStop { position: number; color: string }
export interface SkinAnimationTrack { stops: SkinAnimationStop[]; phaseOffset?: number }
export interface SkinAnimation {
  schemaVersion: 1;
  enabled: boolean;
  cycleSeconds: number;
  pattern: SkinAnimationPattern;
  tracks: Partial<Record<SkinColorField, SkinAnimationTrack>>;
}

export interface RuntimeSkinAnimation {
  schemaVersion: 1;
  enabled: boolean;
  cycleSeconds: number;
  frameCount: number;
  pattern: SkinAnimationPattern;
  fields: Partial<Record<SkinColorField, true>>;
  frames: Record<string, Partial<Record<SkinColorField, SkinColor>>>;
}

export interface SkinPreset {
  id: SkinPresetId;
  userId: UserId;
  species: string;
  name: string;
  schemaVersion: 1 | 2;
  female: boolean;
  skinVariation: number;
  patternIndex: number;
  themeIndex: number;
  goreIntensity: number;
  effectMode: SkinEffectMode;
  effectColor1: string;
  effectColor2: string;
  effectColor3: string;
  effectSpeedSeconds: number;
  effectPattern: SkinEffectPattern;
  environmentMode: SkinEnvironmentMode;
  packLivery: boolean;
  liveryCode?: string;
  colors: Partial<Record<SkinColorField, SkinColor>>;
  animation?: SkinAnimation;
  restricted: boolean;
  status: SkinPresetStatus;
  createdAt: Date;
  updatedAt: Date;
  reviewedByUserId?: UserId;
  reviewedAt?: Date;
  reviewReason?: string;
}

export interface CreateSkinPresetInput {
  userId: UserId;
  species: string;
  name: string;
  female: boolean;
  skinVariation: number;
  patternIndex: number;
  themeIndex: number;
  goreIntensity?: number;
  effectMode?: SkinEffectMode;
  effectColor1?: string;
  effectColor2?: string;
  effectColor3?: string;
  effectSpeedSeconds?: number;
  effectPattern?: SkinEffectPattern;
  environmentMode?: SkinEnvironmentMode;
  packLivery?: boolean;
  liveryCode?: string;
  colors: Partial<Record<SkinColorField, SkinColor>>;
  animation?: SkinAnimation;
  maxPatternIndex: number;
  maxThemeIndex: number;
  restricted: boolean;
  canCreateRestricted: boolean;
  now: Date;
}

export function createSkinPreset(ids: IdGenerator, input: CreateSkinPresetInput): SkinPreset {
  const species = input.species.trim();
  const name = input.name.trim();
  if (!species || species.length > 80) throw new Error("Skin species is required and must be at most 80 characters.");
  if (name.length < 2 || name.length > 60) throw new Error("Skin preset name must be between 2 and 60 characters.");
  if (!Number.isSafeInteger(input.patternIndex) || input.patternIndex < 0 || input.patternIndex > input.maxPatternIndex) {
    throw new Error("Pattern index is outside the verified range for this species.");
  }
  if (!Number.isFinite(input.skinVariation)
    || !Number.isSafeInteger(input.themeIndex)
    || input.themeIndex < 0
    || input.themeIndex > input.maxThemeIndex) {
    throw new Error("Skin variation and theme index are invalid.");
  }
  const goreIntensity = input.goreIntensity ?? 0;
  if (!Number.isFinite(goreIntensity) || goreIntensity < 0 || goreIntensity > 100) {
    throw new Error("Gore intensity must be between 0 and 100 percent.");
  }
  const effectMode = input.effectMode ?? "none";
  if (!skinEffectModes.includes(effectMode)) throw new Error("Skin effect mode is invalid.");
  const effectColor1 = validateEffectHex(input.effectColor1 ?? "#ff315c");
  const effectColor2 = validateEffectHex(input.effectColor2 ?? "#20e3a2");
  const effectColor3 = validateEffectHex(input.effectColor3 ?? "#4f7cff");
  const effectSpeedSeconds = input.effectSpeedSeconds ?? 30;
  if (!Number.isFinite(effectSpeedSeconds) || effectSpeedSeconds < 8 || effectSpeedSeconds > 120) {
    throw new Error("Skin effect speed must be between 8 and 120 seconds.");
  }
  const effectPattern = input.effectPattern ?? "cycle";
  if (!skinEffectPatterns.includes(effectPattern)) throw new Error("Skin effect pattern is invalid.");
  const environmentMode = input.environmentMode ?? "adaptive";
  if (!skinEnvironmentModes.includes(environmentMode)) throw new Error("Skin environment mode is invalid.");
  const packLivery = input.packLivery === true;
  const liveryCode = input.liveryCode?.trim().toUpperCase();
  if (packLivery && !/^[A-Z2-9]{7}$/.test(liveryCode ?? "")) throw new Error("Pack livery code is invalid.");
  if (input.restricted && !input.canCreateRestricted) throw new Error("Restricted skin creation requires dedicated permission.");

  const colors: SkinPreset["colors"] = {};
  for (const field of skinColorFields) {
    const color = input.colors[field];
    if (!color) continue;
    colors[field] = validateColor(color, input.restricted);
  }
  if (!Object.keys(colors).length) throw new Error("At least one supported skin color is required.");
  const animation = input.animation ? validateSkinAnimation(input.animation) : undefined;
  if (effectMode === "hdr_glitch" && animation?.enabled) {
    throw new Error("Prismatic HDR cannot be combined with animated skin regions.");
  }

  return {
    id: ids.next("skin") as SkinPresetId,
    userId: input.userId,
    species,
    name,
    schemaVersion: animation?.enabled ? 2 : 1,
    female: input.female,
    skinVariation: Math.floor(input.skinVariation),
    patternIndex: input.patternIndex,
    themeIndex: input.themeIndex,
    goreIntensity: Math.round(goreIntensity),
    effectMode,
    effectColor1,
    effectColor2,
    effectColor3,
    effectSpeedSeconds: Math.round(effectSpeedSeconds),
    effectPattern,
    environmentMode,
    packLivery,
    ...(packLivery && liveryCode ? { liveryCode } : {}),
    colors,
    ...(animation ? { animation } : {}),
    restricted: input.restricted,
    status: input.restricted ? "pending_review" : "draft",
    createdAt: input.now,
    updatedAt: input.now
  };
}

export function skinAnimationShadeCount(cycleSeconds: number): number {
  return Math.max(16, Math.min(96, Math.round(cycleSeconds)));
}

export function validateSkinAnimation(input: SkinAnimation): SkinAnimation {
  if (input.schemaVersion !== 1) throw new Error("Skin animation schema is not supported.");
  if (!Number.isFinite(input.cycleSeconds) || input.cycleSeconds < 8 || input.cycleSeconds > 120) {
    throw new Error("Skin animation cycle must be between 8 and 120 seconds.");
  }
  if (!skinAnimationPatterns.includes(input.pattern)) throw new Error("Skin animation pattern is invalid.");
  const tracks: SkinAnimation["tracks"] = {};
  for (const field of skinColorFields) {
    const track = input.tracks[field];
    if (!track) continue;
    if (track.stops.length < 2 || track.stops.length > 6) {
      throw new Error(`${field} animation must contain between 2 and 6 colours.`);
    }
    let prior = -1;
    const stops = track.stops.map((stop) => {
      const position = Number(stop.position);
      if (!Number.isFinite(position) || position < 0 || position > 1 || position <= prior) {
        throw new Error(`${field} animation colour positions must increase from 0 to 1.`);
      }
      prior = position;
      return { position, color: validateEffectHex(stop.color) };
    });
    const phaseOffset = Number(track.phaseOffset ?? 0);
    if (!Number.isFinite(phaseOffset) || phaseOffset < 0 || phaseOffset > 1) {
      throw new Error(`${field} animation phase must be between 0 and 1.`);
    }
    tracks[field] = { stops, phaseOffset };
  }
  if (input.enabled && Object.keys(tracks).length === 0) {
    throw new Error("Select at least one skin region to animate.");
  }
  return {
    schemaVersion: 1,
    enabled: input.enabled,
    cycleSeconds: Math.round(input.cycleSeconds),
    pattern: input.pattern,
    tracks
  };
}

export function bakeSkinAnimation(input: SkinAnimation): RuntimeSkinAnimation {
  const animation = validateSkinAnimation(input);
  const frameCount = skinAnimationShadeCount(animation.cycleSeconds);
  const frames: RuntimeSkinAnimation["frames"] = {};
  const fields: RuntimeSkinAnimation["fields"] = {};
  for (const field of skinColorFields) if (animation.tracks[field]) fields[field] = true;
  for (let index = 0; index < frameCount; index += 1) {
    const frame: Partial<Record<SkinColorField, SkinColor>> = {};
    const baseProgress = index / frameCount;
    for (const field of skinColorFields) {
      const track = animation.tracks[field];
      if (!track) continue;
      let progress = (baseProgress + (track.phaseOffset ?? 0)) % 1;
      if (animation.pattern === "pulse") progress = (1 - Math.cos(progress * Math.PI * 2)) / 2;
      if (animation.pattern === "cascade") progress = (progress + skinColorFields.indexOf(field) * 0.073) % 1;
      frame[field] = sampleAnimationTrack(track, progress);
    }
    frames[`Frame${String(index + 1).padStart(2, "0")}`] = frame;
  }
  return { schemaVersion: 1, enabled: animation.enabled, cycleSeconds: animation.cycleSeconds, frameCount, pattern: animation.pattern, fields, frames };
}

function sampleAnimationTrack(track: SkinAnimationTrack, progress: number): SkinColor {
  const stops = track.stops;
  let left = stops[0]!;
  let right = stops.at(-1)!;
  for (let index = 0; index < stops.length - 1; index += 1) {
    if (progress >= stops[index]!.position && progress <= stops[index + 1]!.position) {
      left = stops[index]!; right = stops[index + 1]!; break;
    }
  }
  if (progress < stops[0]!.position || progress > stops.at(-1)!.position) {
    left = stops.at(-1)!;
    right = stops[0]!;
    const wrapped = progress < right.position ? progress + 1 : progress;
    const width = (right.position + 1) - left.position;
    return mixHexColours(left.color, right.color, width <= 0 ? 0 : (wrapped - left.position) / width);
  }
  const width = right.position - left.position;
  return mixHexColours(left.color, right.color, width <= 0 ? 0 : (progress - left.position) / width);
}

function mixHexColours(left: string, right: string, amount: number): SkinColor {
  const channel = (value: string, offset: number) => Number.parseInt(value.slice(offset, offset + 2), 16) / 255;
  const linear = (value: number) => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  const mix = (a: number, b: number) => {
    const progress = Math.max(0, Math.min(1, amount));
    return linear(a) + (linear(b) - linear(a)) * progress;
  };
  return { r: mix(channel(left, 1), channel(right, 1)), g: mix(channel(left, 3), channel(right, 3)), b: mix(channel(left, 5), channel(right, 5)), a: 1 };
}

function validateEffectHex(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(normalized)) throw new Error("Skin effect colours must use six-digit hex values.");
  return normalized;
}

export function reviewSkinPreset(
  preset: SkinPreset,
  input: { reviewerUserId: UserId; decision: "approved" | "rejected" | "disabled"; reason: string; now: Date }
): SkinPreset {
  const reason = input.reason.trim();
  if (reason.length < 3 || reason.length > 280) throw new Error("A review reason between 3 and 280 characters is required.");
  const validTransition = preset.status === "pending_review"
    ? input.decision === "approved" || input.decision === "rejected"
    : preset.status === "approved" && input.decision === "disabled";
  if (!validTransition) throw new Error(`Skin preset cannot move from ${preset.status} to ${input.decision}.`);
  return {
    ...preset,
    status: input.decision,
    reviewedByUserId: input.reviewerUserId,
    reviewedAt: input.now,
    reviewReason: reason,
    updatedAt: input.now
  };
}

function validateColor(color: SkinColor, restricted: boolean): SkinColor {
  const maximum = restricted ? 8 : 1;
  for (const value of [color.r, color.g, color.b, color.a]) {
    if (!Number.isFinite(value) || value < 0 || value > maximum) {
      throw new Error(restricted ? "Restricted color channels must be between 0 and 8." : "Standard color channels must be between 0 and 1.");
    }
  }
  return { ...color };
}
